import { ACCESS_TOKEN_KEY, USER_ROLE } from '@/constants';

/**
 * Write-side helpers (and the key constants they write) for
 * `shared/auth/Authentication.hooks.tsx`'s three non-Cognito auth types
 * (`token`, `jwt`, `login`) — a separate mechanism from
 * `services/auth/cognito.ts`'s Cognito flow, with its own backend
 * (`config.app.auth.host`, not an IdP) and its own key set. Kept in its own
 * file rather than folded into `tokens.ts` (documented as Cognito-specific)
 * or `cognito.ts` (wrong flow entirely).
 *
 * The key constants live here now, not in `Authentication.hooks.tsx` — that
 * file re-exports them for the ~20 other files that import them from there,
 * so no call site needed to change.
 */

export const AUTH_KEY = 'authenticated';
export const AUTH_EXPIRATION_DATE_KEY = 'subscription-expiration-date';
export const AUTH_SUBJECT_KEY = 'authentication-subject';

// Used by the jwt authentication method
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const ACCESS_TOKEN_EXPIRATION_KEY = 'access_token_expiration_date';
export const REFRESH_TOKEN_EXPIRATION_KEY = 'refresh_token_expiration_date';
export const FEATURES_KEY = 'features';
export const IS_SUBSCRIBED_KEY = 'is_subscribed';

/** `token`-type link auth: `authWithToken` in Authentication.hooks.tsx. */
export function saveTokenLinkSession(expirationDate: string): void {
	localStorage.setItem(AUTH_KEY, 'true');
	localStorage.setItem(AUTH_EXPIRATION_DATE_KEY, expirationDate);
}

/**
 * The exact 8-key list `logoutUserJwt`'s `jwtKeys` cleared — deliberately
 * not `FEATURES_KEY`/`IS_SUBSCRIBED_KEY` (unlike Cognito's `clearTokens`),
 * kept as its own constant rather than reused to avoid silently clearing
 * more than the original did.
 */
const DIRECT_AUTH_SESSION_KEYS = [
	AUTH_KEY,
	USER_ROLE,
	ACCESS_TOKEN_KEY,
	REFRESH_TOKEN_KEY,
	AUTH_SUBJECT_KEY,
	AUTH_EXPIRATION_DATE_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
];

export function clearDirectAuthSession(): void {
	for (const key of DIRECT_AUTH_SESSION_KEYS) {
		if (localStorage.getItem(key)) {
			localStorage.removeItem(key);
		}
	}
}

/** `jwt`-type auth: `authenticateViaJwt` in Authentication.hooks.tsx. */
export function saveJwtAuthSession(session: JwtAuthSession): void {
	localStorage.setItem(AUTH_KEY, 'true');
	localStorage.setItem(ACCESS_TOKEN_KEY, session.accessToken);
	localStorage.setItem(
		ACCESS_TOKEN_EXPIRATION_KEY,
		`${session.accessExpirationDate}`,
	);

	localStorage.setItem(REFRESH_TOKEN_KEY, session.refreshToken);
	if (session.refreshExpirationDate) {
		localStorage.setItem(
			REFRESH_TOKEN_EXPIRATION_KEY,
			session.refreshExpirationDate,
		);
	}

	localStorage.setItem(AUTH_EXPIRATION_DATE_KEY, session.subExpirationDate);
	localStorage.setItem(AUTH_SUBJECT_KEY, session.sub);
	localStorage.setItem(FEATURES_KEY, JSON.stringify(session.features));
}

export interface JwtAuthSession {
	accessToken: string;
	accessExpirationDate: string | null;
	refreshToken: string;
	refreshExpirationDate?: string | null;
	subExpirationDate: string;
	sub: string;
	features: Array<string>;
}

/** `login`/`token`-type refresh: `refreshAccessToken` in Authentication.hooks.tsx. */
export function saveLoginRefreshSession(session: LoginRefreshSession): void {
	localStorage.setItem(AUTH_KEY, 'true');
	localStorage.setItem(ACCESS_TOKEN_KEY, session.accessToken);
	localStorage.setItem(AUTH_EXPIRATION_DATE_KEY, session.subExpirationDate);
	localStorage.setItem(
		ACCESS_TOKEN_EXPIRATION_KEY,
		`${session.accessExpirationDate}`,
	);
	localStorage.setItem(AUTH_SUBJECT_KEY, session.sub);
}

export interface LoginRefreshSession {
	accessToken: string;
	subExpirationDate: string;
	accessExpirationDate: string | null;
	sub: string;
}

/** `login`-type auth: `authenticateViaLogin` in Authentication.hooks.tsx. */
export function saveLoginUser(userResponse: unknown): void {
	localStorage.setItem('user', JSON.stringify(userResponse));
}
